import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import classNames from 'classnames'
import IconWrapper from 'components/common/IconWrapper/IconWrapper'
import MainLogo from 'components/assets/icons/MainLogo'
import ResponseMessage from 'components/common/ResponseMessage/ResponseMessage'
import Input from 'components/common/Input/Input'
import Link from 'components/common/Link/Link'
import Button from 'components/common/Button/Button'
import EyeOpen from 'components/assets/icons/EyeOpen'
import EyeClosed from 'components/assets/icons/EyeClosed'
import { emailValidation, passwordValidation } from 'constants/inputValidation'
import api from 'helpers/api'
import style from '../Auth.module.scss'
import commonStyle from '../../../Wrapper/wrapper.module.scss'

const AuthReporting = () => {
  const navigate = useNavigate()
  const { t, i18n: { language } } = useTranslation()
  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const [searchParams] = useSearchParams()
  const message = searchParams.get('message')
  const [errorMessage, setErrorMessage] = useState(message === 'expired' ? 'expired' : null)

  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    trigger
  } = useForm({
    criteriaMode: 'all',
    mode: 'onTouched',
    delayError: 0,
    values: {
      email: '',
      password: ''
    },
  })

  useEffect(() => {
    trigger()
  }, [language, trigger])

  const onSubmit = handleSubmit(async data => {
    try {
      const res = await api.post({ url: '/auth/reporting/signin', data })

      if (res?.success && res.response.reportToken) {
        localStorage.setItem('reportToken', res.response.reportToken)
        navigate('/reporting/overview')
      } else {
        setErrorMessage('failed')
      }
    } catch (error) {
      setErrorMessage(error.message)
    }
  })

  const messageSelection = () => {
    let message = ''
    switch (errorMessage){
      case 'Wrong password':
        message = t('main:auth.signIn.wrongPwd')
        break;
      case 'Sign up unconfirmed':
        message = t('main:auth.signIn.signUpUnconfirmed')
        break;
      case 'User not registered':
        message = t('main:auth.signIn.unknownUser')
        break;
      case 'Unauthorized':
        message = t('main:auth.signIn.unauthorized')
        break;
      case 'Too many requests':
        message = t('main:auth.signIn.tooManyRequests')
        break;
      case 'Session already active':
        message = t('main:auth.signIn.sessionActive')
        break;
      case 'expired':
        message = t('main:auth.signIn.sessionExpired')
        break;
      default:
        message = t('main:errorMessages.general')
        break;
    }
    return message
  }

  return (
    <section className={style.content}>
      <IconWrapper icon={MainLogo} className={style.logo} styles={{ fill: '#000093' }} text='Logo' />
      <h1 className={style.title}>{t('reporting:auth.title')}</h1>

      {errorMessage &&
        <ResponseMessage
          className={style.message}
          label={messageSelection()}
        />
      }

      <form onSubmit={onSubmit}>
        <Input
          register={register('email', emailValidation(t))}
          label={t('reporting:auth.email.label')+'*'}
          autocomplete="email"
          error={errors.email}
          className={commonStyle.input}
        />

        <Input
          register={register('password', passwordValidation(t))}
          label={t('main:auth.password.password')}
          placeholder={t('main:auth.password.placeholder')}
          type={isPasswordShown ? 'text' : 'password'}
          error={errors.password}
          className={commonStyle.input}
          suffix={
            isPasswordShown ? (
              <EyeClosed className={classNames(style.eyeClosed, 'clickable')} />
            ) : (
              <EyeOpen className={classNames(style.eyeOpen, 'clickable')} />
            )
          }
          onSuffixClick={() => setIsPasswordShown(!isPasswordShown)}
        />

        <Link
          label={t('reporting:auth.password.forgotten')}
          className={style.link}
          to={'/reporting/password-lost'}
        />

        <Link
          label={t('main:auth.signIn.createAccount')}
          className={style.link}
          to={'/reporting/signup'}
        />

        <Button
          className={style.button}
          disabled={!isValid}
          label={t('reporting:auth.button')}
          onClick={handleSubmit}
        />
      </form>
    </section>
  )
};

export default AuthReporting
